import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { getCsrfToken, postJson, deleteRequest } from "../../utils/api"

export default class extends Controller {
  static targets = ["item", "handle"]
  static values = { categoryId: Number }

  connect() {
    this.initSortable()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  initSortable() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: '[data-top-ten-sortable-target="handle"]',
      ghostClass: 'sortable-ghost',
      preventOnFilter: false,
      onStart: (event) => {
        // Prevent any link clicks during drag
        event.item.querySelectorAll('a').forEach(link => {
          link.classList.add('no-pointer-events')
        })
      },
      onEnd: (event) => {
        // Re-enable links after drag
        event.item.querySelectorAll('a').forEach(link => {
          link.classList.remove('no-pointer-events')
        })
        this.handleReorder(event)
      }
    })
  }

  handleReorder(event) {
    // Get new order of IDs
    const orderedIds = Array.from(this.itemTargets).map(item => item.dataset.id)

    // Update position badges immediately for visual feedback
    this.updatePositionBadges()

    // Send to server
    this.saveOrder(orderedIds)
  }

  saveOrder(orderedIds) {
    const categoryId = this.hasCategoryIdValue ? this.categoryIdValue : null

    postJson('/favourites/reorder_top_ten', {
      ordered_ids: orderedIds,
      category_id: categoryId
    })
    .then(data => {
      if (!data.success) {
        console.error('Failed to reorder:', data.errors)
      }
    })
    .catch(error => {
      console.error('Error reordering:', error)
    })
  }

  updatePositionBadges() {
    this.itemTargets.forEach((item, index) => {
      const badge = item.querySelector('.badge')
      if (badge) {
        badge.textContent = index + 1
      }
    })
  }

  removeItem(event) {
    event.preventDefault()
    const button = event.currentTarget
    const topTenItemId = button.dataset.topTenItemId
    const listItem = button.closest('li')

    deleteRequest(`/favourites/${topTenItemId}/remove_from_top_ten`)
    .then(data => {
      if (data.success) {
        // Fade out and remove from DOM using CSS classes
        listItem.classList.add('fade-out-right', 'is-fading')

        setTimeout(() => {
          listItem.remove()

          // Update the star icon back to outline in favourites list
          this.updateFavouriteStar(data.favourite_id, false)

          // Update badge count and position numbers
          this.updateBadgeCount()
          this.updatePositionBadges()

          // Add the item back to the favourites list
          if (data.html) {
            this.insertFavouriteItem(data.html)
          }

          // If no items left, show placeholder text
          const remainingItems = this.element.querySelectorAll('li[data-top-ten-sortable-target="item"]')
          if (remainingItems.length === 0) {
            this.showPlaceholder()
          }
        }, 300)
      } else {
        console.error('Failed to remove:', data.errors)
        alert('Could not remove from Top 10')
      }
    })
    .catch(error => {
      console.error('Error removing:', error)
      alert('An error occurred')
    })
  }

  updateFavouriteStar(favouriteId, isFilled) {
    // Find the star button for this favourite in the favourites list
    const starButton = document.querySelector(`[data-add-to-top-ten-favourite-id-value="${favouriteId}"]`)
    if (!starButton) return

    const icon = starButton.querySelector('i')
    if (!icon) return

    if (isFilled) {
      icon.classList.remove('fa-regular')
      icon.classList.add('fa-solid')
    } else {
      icon.classList.remove('fa-solid')
      icon.classList.add('fa-regular')
    }

    // Update the data attribute
    starButton.dataset.addToTopTenInTopTenValue = isFilled.toString()

    // Add scale animation using CSS class
    icon.classList.add('is-pulsing')
    setTimeout(() => {
      icon.classList.remove('is-pulsing')
    }, 200)
  }

  updateBadgeCount() {
    const count = this.element.querySelectorAll('li[data-top-ten-sortable-target="item"]').length
    const header = this.element.previousElementSibling

    if (!header) return

    let badge = header.querySelector('.badge')

    if (count > 0) {
      if (!badge) {
        // Create badge if it doesn't exist
        badge = document.createElement('div')
        badge.className = 'badge'
        header.appendChild(badge)
      }
      badge.textContent = count
    } else if (badge) {
      // Remove badge when count is 0
      badge.remove()
    }
  }

  showPlaceholder() {
    // Show placeholder text when Top 10 is empty
    const placeholder = this.element.nextElementSibling
    if (placeholder && placeholder.classList.contains('has-text-meta')) {
      placeholder.classList.remove('is-hidden')
    }
  }

  insertFavouriteItem(html) {
    // Find the favourites list
    const favouritesList = document.querySelector('ul#favourites-list')
    if (!favouritesList) return

    // Hide the "no favourites" placeholder if visible
    const placeholder = favouritesList.nextElementSibling
    if (placeholder && placeholder.classList.contains('has-text-meta')) {
      placeholder.classList.add('is-hidden')
    }

    // Create the new item
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html.trim()
    const newItem = tempDiv.firstElementChild

    if (!newItem) return

    // Add fade-in animation
    newItem.classList.add('fade-in-up')

    // Insert at the beginning of the list
    favouritesList.insertBefore(newItem, favouritesList.firstChild)

    // Trigger animation
    setTimeout(() => {
      newItem.classList.add('is-visible')
    }, 10)

    // Update the favourites count badge
    this.updateFavouritesCount(1)
  }

  updateFavouritesCount(delta) {
    // Find the Favs section header badge
    const favsHeader = document.querySelector('#favs-section-header')
    if (!favsHeader) return

    const badge = favsHeader.querySelector('.badge')
    if (badge) {
      const currentCount = parseInt(badge.textContent, 10) || 0
      badge.textContent = currentCount + delta
    }
  }
}
